import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { confirmAction } from '../../components/ConfirmDialog';
import { adminAPI } from '../../services/api';
import { FaUsers, FaSearch, FaEdit, FaBan, FaCheckCircle, FaTimesCircle, FaArrowLeft, FaPlus, FaTimes } from 'react-icons/fa';
import {
    DashboardHeader,
    StatusBadge,
    LoadingSpinner,
    EmptyState
} from '../../components/dashboard';

const ManageUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [creating, setCreating] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        username: '',
        password: '',
        first_name: '',
        surname: '',
        role: 'applicant',
        phone: ''
    });
    const [formErrors, setFormErrors] = useState({});
    const usersPerPage = 20;

    useEffect(() => {
        loadUsers();
    }, [currentPage, roleFilter, searchTerm]);

    const loadUsers = async () => {
        try {
            setLoading(true);
            const filters = {
                limit: usersPerPage,
                offset: (currentPage - 1) * usersPerPage
            };
            if (roleFilter) filters.role = roleFilter;
            if (searchTerm) filters.search = searchTerm;

            const response = await adminAPI.getAllUsers(filters);
            setUsers(response.users || []);
            setTotalUsers(response.total || response.users?.length || 0);
        } catch (err) {
            console.error('Failed to load users:', err);
            toast.error('Failed to load users. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        const confirmed = await confirmAction(`Change user role to ${newRole}?`);
        if (!confirmed) return;

        try {
            await adminAPI.updateUserRole(userId, newRole);
            toast.success('User role updated successfully!');
            loadUsers();
        } catch (err) {
            console.error('Failed to update role:', err);
            toast.error('Failed to update user role. Please try again.');
        }
    };

    const handleStatusToggle = async (userId, currentStatus) => {
        const newStatus = !currentStatus;
        const action = newStatus ? 'activate' : 'suspend';
        const confirmed = await confirmAction(`Are you sure you want to ${action} this user?`);
        if (!confirmed) return;

        try {
            await adminAPI.toggleUserStatus(userId, newStatus);
            toast.success(`User ${action}d successfully!`);
            loadUsers();
        } catch (err) {
            console.error('Failed to update status:', err);
            toast.error('Failed to update user status. Please try again.');
        }
    };

    const roles = ['applicant', 'student', 'alumni', 'student_manager', 'employer', 'admin', 'member'];

    const handleCreateUser = async (e) => {
        e.preventDefault();
        
        // Validate form
        const errors = {};
        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'Please enter a valid email';
        }
        
        if (!formData.username.trim()) {
            errors.username = 'Username is required';
        } else if (formData.username.length < 3 || formData.username.length > 20) {
            errors.username = 'Username must be 3-20 characters';
        } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
            errors.username = 'Username can only contain letters, numbers, underscores, or hyphens';
        }
        
        if (!formData.password) {
            errors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            errors.password = 'Password must be at least 8 characters';
        } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])/.test(formData.password)) {
            errors.password = 'Password must contain uppercase, lowercase, number and special character';
        }
        
        if (!formData.first_name.trim()) {
            errors.first_name = 'First name is required';
        }
        
        if (!formData.surname.trim()) {
            errors.surname = 'Surname is required';
        }
        
        if (!formData.role) {
            errors.role = 'Role is required';
        }
        
        setFormErrors(errors);
        if (Object.keys(errors).length > 0) {
            return;
        }
        
        try {
            setCreating(true);
            await adminAPI.createUser(formData);
            toast.success('User created successfully!');
            setShowCreateModal(false);
            setFormData({
                email: '',
                username: '',
                password: '',
                first_name: '',
                surname: '',
                role: 'applicant',
                phone: ''
            });
            setFormErrors({});
            loadUsers();
        } catch (err) {
            console.error('Failed to create user:', err);
            const errorMsg = err?.response?.data?.error || err?.message || 'Failed to create user. Please try again.';
            toast.error(errorMsg);
        } finally {
            setCreating(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-6 py-8">
                <Link to="/dashboard?tab=users" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors">
                    <FaArrowLeft className="text-sm" />
                    <span className="font-medium">Back to Dashboard</span>
                </Link>

                <div className="mb-6">
                    <div className="flex items-start justify-between mb-4">
                        <DashboardHeader
                            icon={FaUsers}
                            title="User Management"
                            subtitle="Manage all users, roles, and account status"
                        />
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium mt-2"
                        >
                            <FaPlus /> Create User
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="relative">
                            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by name, email, or username..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <select
                            value={roleFilter}
                            onChange={(e) => {
                                setRoleFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">All Roles</option>
                            {roles.map(role => (
                                <option key={role} value={role}>{role.replace('_', ' ').toUpperCase()}</option>
                            ))}
                        </select>
                        <div className="text-sm text-gray-600 flex items-center">
                            Total Users: <span className="font-bold ml-2">{totalUsers}</span>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <LoadingSpinner />
                ) : users.length === 0 ? (
                    <EmptyState
                        icon={FaUsers}
                        message="No users found. Try adjusting your search or filters."
                    />
                ) : (
                    <>
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registered</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {users.map((user) => (
                                            <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <div className="font-medium text-gray-900">
                                                            {user.first_name} {user.surname}
                                                        </div>
                                                        <div className="text-sm text-gray-500">{user.email}</div>
                                                        {user.username && (
                                                            <div className="text-xs text-gray-400">@{user.username}</div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <select
                                                        value={user.role}
                                                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                                        className="px-3 py-1 rounded text-sm font-medium border border-gray-300 focus:ring-2 focus:ring-blue-500"
                                                    >
                                                        {roles.map(role => (
                                                            <option key={role} value={role}>
                                                                {role.replace('_', ' ')}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <StatusBadge 
                                                        status={user.email_verified === 1 || user.is_verified === 1 ? 'verified' : 'unverified'} 
                                                    />
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <button
                                                        onClick={() => handleStatusToggle(user.id, user.email_verified === 1)}
                                                        className={`px-3 py-1 rounded text-xs font-medium ${
                                                            user.email_verified === 1 || user.is_verified === 1
                                                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                                                        }`}
                                                    >
                                                        {user.email_verified === 1 || user.is_verified === 1 ? (
                                                            <>
                                                                <FaBan className="inline mr-1" /> Suspend
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FaCheckCircle className="inline mr-1" /> Activate
                                                            </>
                                                        )}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {totalUsers > usersPerPage && (
                                <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
                                    <div className="text-sm text-gray-700">
                                        Showing {(currentPage - 1) * usersPerPage + 1} to {Math.min(currentPage * usersPerPage, totalUsers)} of {totalUsers} users
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => setCurrentPage(p => p + 1)}
                                            disabled={currentPage * usersPerPage >= totalUsers}
                                            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* Create User Modal */}
                {showCreateModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                                <h2 className="text-2xl font-bold text-gray-900">Create New User</h2>
                                <button
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        setFormData({
                                            email: '',
                                            username: '',
                                            password: '',
                                            first_name: '',
                                            surname: '',
                                            role: 'applicant',
                                            phone: ''
                                        });
                                        setFormErrors({});
                                    }}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <FaTimes className="text-xl" />
                                </button>
                            </div>
                            
                            <form onSubmit={handleCreateUser} className="p-6 space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            First Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.first_name}
                                            onChange={(e) => {
                                                setFormData({ ...formData, first_name: e.target.value });
                                                if (formErrors.first_name) setFormErrors({ ...formErrors, first_name: '' });
                                            }}
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                formErrors.first_name ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        />
                                        {formErrors.first_name && (
                                            <p className="text-red-500 text-sm mt-1">{formErrors.first_name}</p>
                                        )}
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Surname <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.surname}
                                            onChange={(e) => {
                                                setFormData({ ...formData, surname: e.target.value });
                                                if (formErrors.surname) setFormErrors({ ...formErrors, surname: '' });
                                            }}
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                formErrors.surname ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        />
                                        {formErrors.surname && (
                                            <p className="text-red-500 text-sm mt-1">{formErrors.surname}</p>
                                        )}
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Email <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => {
                                            setFormData({ ...formData, email: e.target.value });
                                            if (formErrors.email) setFormErrors({ ...formErrors, email: '' });
                                        }}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            formErrors.email ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    />
                                    {formErrors.email && (
                                        <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
                                    )}
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Username <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.username}
                                        onChange={(e) => {
                                            setFormData({ ...formData, username: e.target.value });
                                            if (formErrors.username) setFormErrors({ ...formErrors, username: '' });
                                        }}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            formErrors.username ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    />
                                    {formErrors.username && (
                                        <p className="text-red-500 text-sm mt-1">{formErrors.username}</p>
                                    )}
                                    <p className="text-gray-500 text-sm mt-1">3-20 characters, letters, numbers, underscores, or hyphens</p>
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Password <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="password"
                                        value={formData.password}
                                        onChange={(e) => {
                                            setFormData({ ...formData, password: e.target.value });
                                            if (formErrors.password) setFormErrors({ ...formErrors, password: '' });
                                        }}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            formErrors.password ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    />
                                    {formErrors.password && (
                                        <p className="text-red-500 text-sm mt-1">{formErrors.password}</p>
                                    )}
                                    <p className="text-gray-500 text-sm mt-1">At least 8 characters with uppercase, lowercase, number and special character</p>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Role <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            value={formData.role}
                                            onChange={(e) => {
                                                setFormData({ ...formData, role: e.target.value });
                                                if (formErrors.role) setFormErrors({ ...formErrors, role: '' });
                                            }}
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                formErrors.role ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        >
                                            {roles.map(role => (
                                                <option key={role} value={role}>
                                                    {role.replace('_', ' ').charAt(0).toUpperCase() + role.replace('_', ' ').slice(1)}
                                                </option>
                                            ))}
                                        </select>
                                        {formErrors.role && (
                                            <p className="text-red-500 text-sm mt-1">{formErrors.role}</p>
                                        )}
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Phone (Optional)
                                        </label>
                                        <input
                                            type="tel"
                                            value={formData.phone}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>
                                
                                <div className="flex gap-4 pt-4 border-t border-gray-200">
                                    <button
                                        type="submit"
                                        disabled={creating}
                                        className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {creating ? 'Creating...' : 'Create User'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowCreateModal(false);
                                            setFormData({
                                                email: '',
                                                username: '',
                                                password: '',
                                                first_name: '',
                                                surname: '',
                                                role: 'applicant',
                                                phone: ''
                                            });
                                            setFormErrors({});
                                        }}
                                        className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ManageUsers;
